import { useEffect, useRef, useState } from "react";
import { Form } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useProjectNavigate } from "../../common/utils/useProjectNavigate";
import useNotification from "../../components/notification/notification";

import {
  SESSION_AUTHENTICATED,
  selectSessionStatus,
  selectUser,
  setUser,
} from "../../store/slices/user";
import { bootstrapProjects } from "../../store/thunks/bootstrapProjects";
import ApiAuth from "../../api/auth";
import LoginView from "./login-view";
import { loginWithPasskey, registerPasskey } from "../../common/utils/passkeys/passkeys";

function Login() {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigation = useProjectNavigate();
  const [inProgress, setProgress] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const sessionStatus = useSelector(selectSessionStatus);
  const user = useSelector(selectUser);

  const [searchParams, setSearchParams] = useSearchParams();
  const { openNotification } = useNotification();

  const oauthErrorShownRef = useRef(false);
  const oauthTokenHandledRef = useRef(false);
  const passkeyPromptedRef = useRef(false);
  const landedRef = useRef(false);

  const [oauthAvailability, setOauthAvailability] = useState({
    google: false,
    microsoft: false,
  });

  useEffect(() => {
    let alive = true;

    ApiAuth.getOauthAvailability()
      .then((res) => {
        const o = res?.data?.oauth || {};
        const google = !!o?.google?.enabled;
        const microsoft = !!o?.microsoft?.enabled;

        if (alive) {
          setOauthAvailability({ google, microsoft });
        }
      })
      .catch(() => {
        // safe default: hide oauth buttons
        if (alive) setOauthAvailability({ google: false, microsoft: false });
      });

    return () => {
      alive = false;
    };
  }, []);

  const promptForPasskeyIfNeeded = async (user) => {
    if (passkeyPromptedRef.current) return;

    if (user && user.promptPasskeySetup) {
      passkeyPromptedRef.current = true;
      try {
        await ApiAuth.recordPasskeyPrompt();
        await registerPasskey();
        openNotification({ type: "success", message: <p>{t("login.passkey.registered")}</p> });
      } catch (e) {
        if (e?.name !== "NotAllowedError") {
          console.error("Passkey prompt failed", e);
          openNotification({ type: "error", message: <p>{t("login.passkey.register_failed")}</p> });
        }
      }
    }
  };

  useEffect(() => {
    const code = searchParams.get("oauthError");
    if (!code) return;

    if (oauthErrorShownRef.current) return;
    oauthErrorShownRef.current = true;

    const map = {
      OAUTH_USER_NOT_FOUND: t("login.oauth.user_not_found"),
      OAUTH_NO_EMAIL: t("login.oauth.no_email"),
      USER_BLOCKED: t("login.oauth.user_blocked"),
      USER_INACTIVE: t("login.oauth.user_inactive"),
      OAUTH_ERROR: t("login.oauth.error"),
      OAUTH_FAILED: t("login.oauth.failed"),
      OAUTH_INTERNAL: t("login.oauth.internal"),
    };

    openNotification({
      type: "error",
      message: <p>{map[code] || t("login.oauth.failed")}</p>,
    });

    const next = new URLSearchParams(searchParams);
    next.delete("oauthError");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openNotification, t]);

  /**
   * LF360-1970 — coming back from Google or Microsoft.
   *
   * The callback sets the session cookie before it redirects here, so there is
   * nothing to store and nothing to send: the session already exists and
   * `useSession` in `App` has read it, or is about to.
   *
   * `oauthToken` is still in the URL because the backend cannot know which
   * portal build is waiting for the redirect. This one ignores it and takes it
   * out of the address bar, so a credential does not sit in the history of a
   * machine that no longer needs it. The backend stops sending it in the
   * release after this one.
   */
  useEffect(() => {
    if (!searchParams.get("oauthToken")) return;

    if (oauthTokenHandledRef.current) return;
    oauthTokenHandledRef.current = true;

    // The other two sign-in paths do this and this one did not, which is the
    // whole of why signing in with Google looked like it did nothing: a token
    // from before the cookie release is still sent as a header, the backend
    // prefers a header over a cookie, and a dead one is refused before the
    // fresh session is ever consulted.
    localStorage.removeItem("user_token");

    const next = new URLSearchParams(searchParams);
    next.delete("oauthToken");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Already signed in — arriving here by typing the URL, by a bookmark, or
   * straight from an OAuth redirect. The session answered before this rendered,
   * so there is nowhere to send them but on.
   */
  useEffect(() => {
    if (sessionStatus !== SESSION_AUTHENTICATED || landedRef.current) return;

    landedRef.current = true;

    dispatch(bootstrapProjects()).finally(() => {
      navigation("/dashboard");
      promptForPasskeyIfNeeded(user);
    });
  }, [sessionStatus, user, dispatch, navigation]);

  const onGoogleLogin = () => {
    const baseUrl = process.env.REACT_APP_DEV_URL || "";

    const raw = localStorage.getItem("current_project_id");
    const projectId = raw ? String(raw) : null;

    const url = new URL(`${baseUrl}/api/v1/auth/oauth/google/start`, window.location.origin);
    if (projectId) url.searchParams.set("projectId", projectId);

    window.location.href = url.toString();
  };

  const onMicrosoftLogin = () => {
    const baseUrl = process.env.REACT_APP_DEV_URL || "";

    const raw = localStorage.getItem("current_project_id");
    const projectId = raw ? String(raw) : null;

    const url = new URL(`${baseUrl}/api/v1/auth/oauth/microsoft/start`, window.location.origin);
    if (projectId) url.searchParams.set("projectId", projectId);

    window.location.href = url.toString();
  };

  const onPasskeyLogin = async () => {
    try {
      setProgress(true);

      const raw = localStorage.getItem("current_project_id");
      const projectId = raw ? String(raw) : null;

      // LF360-1970: verification ends in a cookie, so what comes back is the
      // user and nothing to keep. `session()` is what fills the store, the same
      // way a reload would.
      await loginWithPasskey({ projectId });
      localStorage.removeItem("user_token");

      const signedIn = (await ApiAuth.session()).data.user;

      dispatch(setUser(signedIn));
      dispatch(bootstrapProjects()).finally(() => {
        navigation("/dashboard");
        promptForPasskeyIfNeeded(signedIn);
      });
    } catch (e) {
      if (e?.name === "NotAllowedError") return;
      openNotification({ type: "error", message: <p>{t("login.passkey.failed")}</p> });
    } finally {
      setProgress(false);
    }
  };

  const login = () => {
    setProgress(true);
    const { email, password } = form.getFieldsValue();
    // LF360-1970: the cookie route. Nothing comes back to store, and nothing is
    // stored - the browser holds the session where a script cannot reach it.
    ApiAuth.loginWithCookie({ email, password })
      .then((res) => {
        const { user } = res.data;
        if (user) {
          // Any token left over from before the cookie release would otherwise
          // keep being sent as a header, and the header wins.
          localStorage.removeItem("user_token");
          dispatch(setUser(user));
          dispatch(bootstrapProjects()).finally(() => {
            navigation("/dashboard");
            promptForPasskeyIfNeeded(user);
          });
        } else {
          setErrorMsg(t("login.no_connect"));
        }
      })
      .catch((e) => {
        if (e.response?.data?.error?.message) setErrorMsg(e.response.data.error.message);
      })
      .finally(() => setProgress(false));
  };

  // Nothing to show while the session is still being decided, or once it has
  // come back signed in and the effect above is on its way to the dashboard.
  if (sessionStatus === SESSION_AUTHENTICATED) return null;

  return (
    <LoginView
      form={form}
      t={t}
      inProgress={inProgress}
      errorMsg={errorMsg}
      login={login}
      onGoogleLogin={onGoogleLogin}
      onMicrosoftLogin={onMicrosoftLogin}
      onPasskeyLogin={onPasskeyLogin}
      showGoogle={oauthAvailability.google}
      showMicrosoft={oauthAvailability.microsoft}
    />
  );
}

export default Login;